import { useState, type ReactNode } from "react";
import {
  Platform,
  Text,
  TextInput,
  View,
  type TextInputProps,
  type TextStyle,
} from "react-native";
import { colors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MoonFieldInput } from "./MoonFieldInput";

/**
 * The box a typed value sits in, with its unit inside it.
 *
 * One look for every value that has a unit: the field chrome, the value, and
 * after the value either a fixed unit (`suffix`, such as "mm") or a control
 * (`trailing`, such as a `MoonUnitToggle` for ng/mL or pmol/L). A field keeps
 * the same height, padding and text size whichever it carries, so the AMH
 * value, a lab result and a follicle size read as the same field.
 *
 * `MoonNumericField` renders this box for a number. Use it directly when the
 * value is text (a list of sizes, a lab result that can read "Negative") or
 * when the label belongs to an outer `MoonFormField`.
 *
 * State coverage (Phase 1.0 matrix): idle / focused / error from
 * `MoonFieldInput`; disabled stops editing and dims the text, and the
 * wrapping `MoonFormField` dims the label.
 */
type Props = {
  value: string;
  onChangeText: (text: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  keyboardType?: TextInputProps["keyboardType"];
  hasError?: boolean;
  disabled?: boolean;
  /** Text before the value, such as a currency sign. */
  prefix?: string;
  /** A fixed unit after the value, such as "mm" or "%". */
  suffix?: string;
  /** A control after the value, such as a unit toggle or a unit menu. */
  trailing?: ReactNode;
  /**
   * Holds the focused state while something the field owns has focus, such
   * as a unit menu opened from `trailing`.
   */
  focused?: boolean;
  accessibilityLabel?: string;
  maxLength?: number;
  testID?: string;
  /** Pass-through TextInput props for fine-grained control. */
  textInputProps?: Omit<
    TextInputProps,
    | "value"
    | "onChangeText"
    | "onFocus"
    | "onBlur"
    | "editable"
    | "keyboardType"
    | "placeholder"
    | "placeholderTextColor"
    | "style"
  >;
};

// A trailing control is taller than a line of text. It may reach this far
// into the vertical padding, so it never makes its field taller than one
// without a control.
const TRAILING_OVERHANG = 6;
// The control's inset from the field's right edge, close to the space above
// and below it so it sits evenly inside the rounded corner.
const TRAILING_INSET = 8;

/** The style of a unit written inside a field, after its value. */
export function useFieldUnitTextStyle(): TextStyle {
  const fontSize = useInputFontSize();
  return {
    fontFamily: typography.families.sansMedium,
    fontSize,
    color: colors.textLight,
  };
}

export function MoonValueInput({
  value,
  onChangeText,
  onFocus,
  onBlur,
  placeholder,
  keyboardType,
  hasError,
  disabled,
  prefix,
  suffix,
  trailing,
  focused: focusedProp,
  accessibilityLabel,
  maxLength,
  testID,
  textInputProps,
}: Props) {
  const fontSize = useInputFontSize();
  const unitTextStyle = useFieldUnitTextStyle();
  const [inputFocused, setInputFocused] = useState(false);

  return (
    <MoonFieldInput
      hasError={hasError}
      focused={inputFocused || focusedProp}
      paddingY={12}
      style={trailing ? { paddingRight: TRAILING_INSET } : undefined}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        {prefix ? <Text style={unitTextStyle}>{prefix}</Text> : null}
        <TextInput
          value={value}
          onChangeText={onChangeText}
          onFocus={() => {
            setInputFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setInputFocused(false);
            onBlur?.();
          }}
          editable={!disabled}
          keyboardType={keyboardType}
          placeholder={placeholder}
          placeholderTextColor={colors.textFaint}
          accessibilityLabel={accessibilityLabel}
          maxLength={maxLength}
          testID={testID}
          {...textInputProps}
          // minWidth 0 lets the value give way to the unit beside it.
          style={{
            flex: 1,
            minWidth: 0,
            padding: 0,
            fontFamily: typography.families.sans,
            fontSize,
            color: colors.text,
            opacity: disabled ? 0.6 : 1,
            ...(Platform.OS === "web" ? { outlineStyle: "none" as const } : {}),
          }}
        />
        {suffix ? <Text style={unitTextStyle}>{suffix}</Text> : null}
        {trailing ? (
          <View style={{ marginVertical: -TRAILING_OVERHANG }}>{trailing}</View>
        ) : null}
      </View>
    </MoonFieldInput>
  );
}
